import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { useCan } from '../../contexts/PermissionsContext'
import { useDisclosures, usePersonalData } from '../../hooks/useDataProtection'
import { Badge } from '../ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { AppIcon } from '../ui/icon'
import { DisclosureList } from './DocumentDataProtection'
import { ContactErasureRequest } from './Erasure'
import { RetentionBadge } from './RetentionBadge'

/**
 * "Données personnelles" of a contact (spec 2026-10-03-02, lots 1 to 3): the
 * inventory that answers a right-of-access request, its retention state and
 * the disclosures of its documents. Shown to `data_protection.read`.
 *
 * Collapsed by default (#1766): a rarely used, compliance-only block. The
 * header keeps the title and the retention badge visible; the inventory, the
 * disclosures and the erasure request open on click.
 */
export function ContactPersonalData({ org, userUid }: { org: string; userUid: string }) {
    const { t } = useTranslation('data-protection')
    const canRead = useCan('data_protection.read')
    const inventory = usePersonalData(org, userUid, canRead)
    const disclosures = useDisclosures(org, { subject_user_uid: userUid }, canRead)
    const [open, setOpen] = useState(false)
    if (!canRead) return null
    const pd = inventory.data

    return (
        <Collapsible open={open} onOpenChange={setOpen} asChild>
            <section className="bg-card rounded-lg border border-border p-6" data-testid="contact-personal-data">
                <div className="flex flex-wrap items-center gap-2">
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="flex items-center gap-2 text-left"
                            data-testid="contact-personal-data-toggle"
                        >
                            <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" tone="muted" className="shrink-0" />
                            <h2 className="text-lg font-semibold text-foreground">{t('contact.title')}</h2>
                        </button>
                    </CollapsibleTrigger>
                    {pd?.retention && (
                        <RetentionBadge state={pd.retention.state} until={pd.retention.until} testId="contact-retention-badge" />
                    )}
                </div>
                <CollapsibleContent className="mt-3">
                    {pd && (
                        <>
                            <dl className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="contact-personal-data-counts">
                                {(
                                    [
                                        ['contracts', pd.contracts.length],
                                        ['assets', pd.assets.length],
                                        ['documents', pd.documents.length],
                                        ['extracted_values', pd.extracted_values.length],
                                        ['conversations', pd.conversations.length],
                                        ['issues', pd.issues.length],
                                    ] as const
                                ).map(([key, n]) => (
                                    <div key={key} className="rounded-md border p-2" data-testid={`contact-pd-${key}`}>
                                        <dt className="text-xs text-muted-foreground">{t(`contact.${key}`)}</dt>
                                        <dd className="text-lg font-semibold tabular-nums">{n}</dd>
                                    </div>
                                ))}
                            </dl>
                            {pd.documents.length > 0 && (
                                <ul className="mb-4 space-y-1" data-testid="contact-pd-document-list">
                                    {pd.documents.map((d) => (
                                        <li key={d.uid} className="flex flex-wrap items-center gap-2 text-sm">
                                            <Link to="/$org/documents/$docUid" params={{ org, docUid: d.uid }} className="hover:underline" data-testid={`contact-pd-document-${d.uid}`}>
                                                {d.name}
                                            </Link>
                                            {d.origins.map((o) => (
                                                <Badge key={o} variant="secondary">{t(`origin.${o}`)}</Badge>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {pd.measures.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4" data-testid="contact-pd-measures">
                                    {pd.measures.map((m) => (
                                        <Badge key={m.uid} variant={m.kind === 'hold' ? 'info' : 'warning'}>
                                            {t(`measures.kind.${m.kind}`)} · {t(`measures.reason.${m.reason}`)}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    <h3 className="text-sm font-medium mb-1">{t('disclosures.title')}</h3>
                    <DisclosureList query={disclosures} />
                    <ContactErasureRequest org={org} userUid={userUid} personalData={pd} />
                </CollapsibleContent>
            </section>
        </Collapsible>
    )
}
